import { appLinkTrees } from "@megarax/links";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom-v5-compat";

import { AnyDoc } from "@megaron/docs-contracts";
import { getMegaraxUriFromPath } from "@megaron/megarax-uri";
import { useClientManager } from "@megaron/react-clients";

import { getDocDisplayName } from "../history/getDocDisplayName";
import { resolveDocRef } from "../history/resolveDocRef";
import { isPageLinkActive, linkHref } from "../nav/navHelpers";
import { uriBase } from "./pinnedLinkHelpers";

export type PageLinkRef = {
  uri: string;
  name: string;
};

type ContextValue = {
  registeredLink: PageLinkRef | undefined;
  setRegisteredLink: (link: PageLinkRef | undefined) => void;
};

export const CurrentPageLinkContext = createContext<ContextValue>({
  registeredLink: undefined,
  setRegisteredLink: () => undefined,
});

export const CurrentPageLinkProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [registeredLink, setRegisteredLink] = useState<PageLinkRef | undefined>(undefined);

  const value = useMemo(() => ({ registeredLink, setRegisteredLink }), [registeredLink]);

  return <CurrentPageLinkContext.Provider value={value}>{children}</CurrentPageLinkContext.Provider>;
};

/**
 * Lets a page state the uri and default pin name it wants to be pinned under.
 * Without it the uri is resolved from the path and the name falls back to the
 * nav link name, the document name or the last path segment.
 */
export const useRegisterPageLink = (link: PageLinkRef | undefined) => {
  const { setRegisteredLink } = useContext(CurrentPageLinkContext);
  const uri = link?.uri;
  const name = link?.name;

  useEffect(() => {
    setRegisteredLink(uri && name ? { uri, name } : undefined);

    return () => setRegisteredLink(undefined);
  }, [uri, name, setRegisteredLink]);
};

const navLinkName = (pathname: string): string | undefined => {
  for (const appTree of appLinkTrees) {
    const pageLink = appTree.pageLinks.find((link) => linkHref(link.uri) === pathname);
    if (pageLink) return pageLink.name;

    if (linkHref(appTree.uri) === pathname) return appTree.name;
  }

  return undefined;
};

const activeNavLinkName = (pathname: string, search: string): string | undefined => {
  const searchParams = new URLSearchParams(search);

  for (const appTree of appLinkTrees) {
    const pageLink = appTree.pageLinks.find((link) => isPageLinkActive(link, pathname, searchParams));
    if (pageLink) return pageLink.name;
  }

  return undefined;
};

const pathFallbackName = (pathname: string): string => {
  const segment = pathname.split("/").filter(Boolean).pop();
  if (!segment) return "Strona główna";

  return segment.charAt(0).toUpperCase() + segment.slice(1);
};

export const useCurrentPageUri = (): string => {
  const location = useLocation();

  return useMemo(
    () => getMegaraxUriFromPath(location.pathname, location.search) ?? `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );
};

const useCurrentPageDoc = (pathname: string): { doc: AnyDoc | undefined; isPending: boolean } => {
  const docRef = useMemo(() => resolveDocRef(pathname), [pathname]);

  const docsQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery({ docIds: docRef ? [docRef.docId] : [] }, { enabled: Boolean(docRef), keepPreviousData: true });

  if (!docRef) return { doc: undefined, isPending: false };

  const doc = (docsQuery.data ?? []).find((d): d is AnyDoc => d !== null && d.docId === docRef.docId);

  return { doc, isPending: doc === undefined && docsQuery.isFetching };
};

export const useCurrentPageLink = (): PageLinkRef & { isPending: boolean } => {
  const location = useLocation();
  const { registeredLink } = useContext(CurrentPageLinkContext);
  const uri = useCurrentPageUri();
  const { doc, isPending } = useCurrentPageDoc(location.pathname);

  const docName = doc ? getDocDisplayName(doc).trim() : "";

  return useMemo(() => {
    if (registeredLink) return { ...registeredLink, isPending: false };

    const resolvedName =
      navLinkName(uriBase(location.pathname)) ||
      docName ||
      activeNavLinkName(location.pathname, location.search);

    const name = resolvedName || (isPending ? "" : pathFallbackName(location.pathname));

    return { uri, name, isPending: !resolvedName && isPending };
  }, [registeredLink, uri, docName, isPending, location.pathname, location.search]);
};
